/**
 * Animated QR, both directions — the air-gap path.
 *
 * This is the main road, not a fallback. Coldcard, Keystone, Passport, Jade and SeedSigner have
 * no USB signing path at all; QR is how serious cold storage has always worked, and it is the
 * only path available in Safari, Firefox and on every browser on iOS.
 *
 * Two formats, because the ecosystem has two:
 *   · `ur:crypto-psbt` (BCR-2020-005) — Keystone, Passport, Jade, Sparrow, Nunchuk
 *   · BBQr                            — Coldcard
 *
 * Decoding prefers the platform: `BarcodeDetector` is native in Chromium and costs zero bytes.
 * jsQR is the fallback, and it matters precisely where it is heaviest — Safari and Firefox have
 * no BarcodeDetector and are exactly the browsers whose users depend on QR.
 */
import { UR, UREncoder, URDecoder } from '@gandlaf21/bc-ur';
import { splitQRs, joinQRs } from 'bbqr';
import { renderSVG } from 'uqr';
import jsQR from 'jsqr';
import { toHex } from './psbt-io.js';

export type Format = 'ur' | 'bbqr';

export class QrError extends Error {
  constructor(public readonly code: string, message: string) {
    super(`${code}: ${message}`);
    this.name = 'QrError';
  }
}

/* ── encoding (we display; the device scans) ──────────────────────────────── */

/**
 * Fragment size is a real safety parameter, not a tuning knob.
 *
 * Too large and the QR gains modules until a phone or a Coldcard's camera cannot resolve it —
 * and the failure looks like "the device is broken", not "the QR is too dense". The BCR default
 * of ~200 bytes per fragment keeps each frame comfortably scannable on the small, fixed-focus
 * cameras these devices actually have.
 */
const UR_FRAGMENT_BYTES = 200;

export interface Frames {
  format: Format;
  /** Each entry is the text encoded into one QR frame. */
  parts: string[];
}

export function encodeFrames(psbt: Uint8Array, format: Format): Frames {
  if (format === 'ur') {
    // crypto-psbt is the PSBT bytes as a CBOR byte string under that ur type. `UR.from` does
    // the CBOR wrapping but hardcodes type 'bytes', so the payload is re-tagged here. Sending
    // `ur:bytes` instead of `ur:crypto-psbt` is the classic interop bug: some wallets accept it,
    // Keystone and Passport reject it, and the error the user sees is a shrug.
    const ur = new UR(UR.from(psbt).cbor, 'crypto-psbt');
    const enc = new UREncoder(ur, UR_FRAGMENT_BYTES);
    // Single-part URs encode as `ur:crypto-psbt/<payload>` with no sequence numbers; multi-part
    // adds `/1-3/`. Taking exactly `fragmentsLength` parts yields one clean pass over the data,
    // which every scanner understands. Fountain encoding beyond that is for lossy channels and
    // is generated on demand by the animation loop, not baked in here.
    const parts = Array.from({ length: enc.fragmentsLength }, () => enc.nextPart());
    return { format, parts };
  }

  // BBQr chooses its own encoding (zlib or raw) and splits to fit the version it picks.
  const { parts } = splitQRs(psbt, 'P', { encoding: 'Z', minSplit: 1, maxSplit: 1295 });
  return { format, parts };
}

/**
 * Render one frame to an SVG string.
 *
 * `encode()` returns the module matrix, not markup — calling `.toString()` on it yields
 * "[object Object]", which renders as an empty box and looks exactly like a broken QR.
 * `renderSVG` is the function that emits markup.
 *
 * Error correction stays at L deliberately: these frames are shown on a bright screen a few
 * centimetres from the camera, so there is no damage to correct for, and every level above L
 * spends modules on redundancy that make the code denser and HARDER for a fixed-focus device
 * camera to resolve.
 */
export function frameToSvg(text: string): string {
  return renderSVG(qrText(text), { ecc: 'L', border: 2 });
}

/**
 * Uppercase `ur:` fragments before encoding them.
 *
 * URs are case-insensitive, and every character one uses — 0-9, letters, `:` `/` `-` — falls
 * inside QR's *alphanumeric* mode once uppercased. Lowercase forces byte mode, which spends
 * 8 bits per character instead of 5.5 and produces a visibly denser code for the same payload.
 * BCR-2020-005 recommends uppercase for exactly this reason, and it is why the URs on a
 * Keystone screen are uppercase.
 *
 * BBQr is left alone: its header is already uppercase and its base32 payload is defined
 * case-sensitively.
 */
function qrText(text: string): string {
  return /^ur:/i.test(text) ? text.toUpperCase() : text;
}

/* ── decoding (the device displays; we scan) ──────────────────────────────── */

export interface ScanProgress {
  format: Format | null;
  received: number;
  expected: number | null;
  /**
   * What `received` and `expected` are COUNTING. The two formats cannot answer the same
   * question, and flattening them into one pair of numbers made the UI lie.
   *
   * BBQr splits a PSBT into a known number of parts, so "3 of 7 frames" is a real statement and
   * the last frame really is the last one.
   *
   * UR is a FOUNTAIN code. There is no fixed total: a Keystone cycles indefinitely, frames may
   * repeat, and the decode completes as soon as enough distinct fragments have arrived — which
   * can be at any point. `getProgress()` returns a RATIO, so it was being reported as
   * `received: 33, expected: 100` and rendered "ur · 33 / 100": a frame count with a finish line
   * that does not exist. Somebody watching for frame 100 waits for a number that will never
   * arrive, on a screen that had already finished.
   */
  unit: 'frames' | 'percent';
  /** Set once every fragment has arrived. */
  result: Uint8Array | null;
}

/**
 * Accumulates QR frames until a whole PSBT has arrived.
 *
 * Stateful because animated QR is: a Keystone cycles through fountain-coded fragments and the
 * same frame may be seen several times before a new one appears. Feeding duplicates must be
 * harmless.
 */
export class Scanner {
  private ur: URDecoder | null = null;
  private bbqrParts = new Map<number, string>();
  private bbqrTotal: number | null = null;
  format: Format | null = null;

  /** Feed one decoded QR string. Returns the PSBT once complete, else null. */
  accept(text: string): Uint8Array | null {
    const t = text.trim();
    if (!t) return null;

    if (/^ur:/i.test(t)) {
      this.format = 'ur';
      this.ur ??= new URDecoder();
      this.ur.receivePart(t);
      if (!this.ur.isComplete()) return null;
      if (!this.ur.isSuccess()) throw new QrError('UR_DECODE_FAILED', this.ur.resultError());
      const ur = this.ur.resultUR();
      if (ur.type !== 'crypto-psbt')
        throw new QrError('WRONG_UR_TYPE',
          `expected a crypto-psbt, got ur:${ur.type}. That is a different kind of object — ` +
          `a wallet descriptor or an account export, not a transaction to sign.`);
      return new Uint8Array(ur.decodeCBOR());
    }

    // BBQr frames look like: B$ <enc> <type> <total base36 x2> <index base36 x2> <payload>
    const m = /^B\$([0-9A-Z])([0-9A-Z])([0-9A-Z]{2})([0-9A-Z]{2})/.exec(t);
    if (m) {
      this.format = 'bbqr';
      const total = parseInt(m[3]!, 36);
      const index = parseInt(m[4]!, 36);
      this.bbqrTotal = total;
      this.bbqrParts.set(index, t);
      if (this.bbqrParts.size < total) return null;
      const ordered = Array.from({ length: total }, (_, i) => this.bbqrParts.get(i)!);
      const { raw, fileType } = joinQRs(ordered);
      if (fileType !== 'P')
        throw new QrError('WRONG_BBQR_TYPE',
          `expected a PSBT (type P), got type ${fileType}`);
      return new Uint8Array(raw);
    }

    throw new QrError('UNRECOGNISED_QR',
      `that QR is neither a ur: fragment nor a BBQr frame. First characters: ` +
      `${t.slice(0, 24)}${t.length > 24 ? '…' : ''}`);
  }

  progress(): ScanProgress {
    if (this.format === 'ur' && this.ur) {
      return {
        format: 'ur',
        received: this.ur.getProgress() > 0 ? Math.round(this.ur.getProgress() * 100) : 0,
        expected: 100,
        unit: 'percent',
        result: null,
      };
    }
    if (this.format === 'bbqr') {
      return {
        format: 'bbqr', received: this.bbqrParts.size, expected: this.bbqrTotal,
        unit: 'frames', result: null,
      };
    }
    return { format: null, received: 0, expected: null, unit: 'frames', result: null };
  }

  reset(): void {
    this.ur = null; this.bbqrParts.clear(); this.bbqrTotal = null; this.format = null;
  }
}

/* ── camera ───────────────────────────────────────────────────────────────── */

/**
 * Native first. `BarcodeDetector` is in Chromium and decodes off the main thread for free;
 * jsQR is ~130 KB and exists for Safari and Firefox, which have no BarcodeDetector and whose
 * users have no USB path either — so the heavy fallback lands exactly where it is needed.
 */
export const hasNativeDetector = (): boolean => 'BarcodeDetector' in globalThis;

type Detect = (bitmap: ImageBitmap | HTMLVideoElement) => Promise<string[]>;

export async function makeDetector(): Promise<Detect> {
  if (hasNativeDetector()) {
    const BD = (globalThis as unknown as {
      BarcodeDetector: new (o: { formats: string[] }) => {
        detect(s: CanvasImageSource): Promise<{ rawValue: string }[]>;
      };
    }).BarcodeDetector;
    const det = new BD({ formats: ['qr_code'] });
    return async (src) => (await det.detect(src)).map((b) => b.rawValue);
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  return async (src) => {
    const w = (src as HTMLVideoElement).videoWidth || (src as ImageBitmap).width;
    const h = (src as HTMLVideoElement).videoHeight || (src as ImageBitmap).height;
    if (!w || !h) return [];
    canvas.width = w; canvas.height = h;
    ctx.drawImage(src as CanvasImageSource, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    const hit = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' });
    return hit ? [hit.data] : [];
  };
}

/** Short identity line for a scanned payload, so the user can sanity-check before trusting it. */
export const describe = (bytes: Uint8Array): string =>
  `${bytes.length} bytes · starts ${toHex(bytes.subarray(0, 5))}`;
